import { useEffect, useState } from "react";
import genericRepositoryService from "../../../../services/genericRepositoryService";

export type Language = {
  code: string;
  name: string;
  direction: string;
};

export type LanguageOption = {
  value: string;
  label: string;
};

/**
 * Hook for managing supported translation languages
 * Single Responsibility: Fetch and format language data
 */
export const useTranslationLanguages = () => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setIsLoading(true);
        const response = await genericRepositoryService.getSupportedTranslationLanguages();
        const languagesData = response.data.supportedTranslationLanguages;

        const languagesArray: Language[] = Object.values(languagesData);
        const options: LanguageOption[] = languagesArray.map((lang) => ({
          value: lang.code,
          label: lang.name,
        }));

        setLanguages(languagesArray);
        setLanguageOptions(options);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to load languages"));
        console.error("Failed to load translation languages:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  const getLanguageByCode = (code: string): Language | undefined => {
    return languages.find((lang) => lang.code === code);
  };

  return {
    languages,
    languageOptions: languageOptions.sort((a, b) => a.label.localeCompare(b.label)),
    isLoading,
    error,
    getLanguageByCode,
  };
};
